"use client";

import {Calendar, Mail, Phone, MessageCircle} from "lucide-react";
import Link from "next/link";
import {useState} from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import LeadDialogForm from "@/app/(pages)/dove-siamo/[slug]/_components/LeadDialogForm";
import {CONTACT_WHATSAPP} from "@/lib/public-config";

interface ContactButtonsProps {
    phone: string;
    email: string;
    bgButton?: boolean;
    useLeadDialog?: boolean;
    sedeTitle?: string;
    provincia?: string;
}

export default function ContactButtons({phone, email, bgButton, useLeadDialog = false, sedeTitle = "Sede", provincia}: ContactButtonsProps) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<"default" | "whatsapp" | "info" | "call">("default");

    // Stili per pulsanti bianchi con testo nero
    const whiteButtonClass = bgButton
        ? "flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-lg border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 hover:scale-105 font-medium"
        : "";

    // Stile per il pulsante primario
    const primaryButtonClass =  "flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-300 hover:scale-105 font-medium";

    // Stile per i pulsanti secondari
    const secondaryButtonClass = bgButton
        ? whiteButtonClass
        : "flex items-center justify-center gap-2 px-6 py-3 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-30 rounded-lg text-white hover:bg-opacity-20 transition-all duration-300 hover:scale-105 font-medium";

    return (
        <>
        <div className="flex flex-wrap gap-3 lg:flex-shrink-0">
            {/* Prenota Appuntamento */}
            {useLeadDialog ? (
                <button
                    onClick={() => {
                        setMode("default");
                        setOpen(true);
                    }}
                    className={primaryButtonClass}
                >
                    <Calendar className="w-5 h-5"/>
                    <span>Prenota Appuntamento</span>
                </button>
            ) : (
                <Link hrefLang="it" lang="it" href="#prenota" className={primaryButtonClass}>
                    <Calendar className="w-5 h-5"/>
                    <span>Prenota Appuntamento</span>
                </Link>
            )}

            {/* Ottieni Informazioni */}
            {email && (
                useLeadDialog ? (
                    <button
                        onClick={() => {
                            setMode("info");
                            setOpen(true);
                        }}
                        className={secondaryButtonClass}
                    >
                        <Mail className="w-5 h-5"/>
                        <span>Ottieni Informazioni</span>
                    </button>
                ) : (
                    <a hrefLang="it" lang="it" href={`mailto:${email}`} className={secondaryButtonClass}>
                        <Mail className="w-5 h-5"/>
                        <span>Ottieni Informazioni</span>
                    </a>
                )
            )}

            {/* WhatsApp */}
            {phone && (
                useLeadDialog ? (
                    <button
                        onClick={() => {
                            setMode("whatsapp");
                            setOpen(true);
                        }}
                        className={secondaryButtonClass}
                    >
                        <MessageCircle className="w-5 h-5"/>
                        <span>WhatsApp</span>
                    </button>
                ) : (
                    <Link
                        hrefLang="it"
                        lang="it"
                        href={`https://wa.me/${CONTACT_WHATSAPP}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={secondaryButtonClass}
                    >
                        <MessageCircle className="w-5 h-5"/>
                        <span>WhatsApp</span>
                    </Link>
                )
            )}

            {/* Chiama */}
            {phone && (
                useLeadDialog ? (
                    <button
                        onClick={() => {
                            setMode("call");
                            setOpen(true);
                        }}
                        className={secondaryButtonClass}
                    >
                        <Phone className="w-5 h-5"/>
                        <span>Chiama</span>
                    </button>
                ) : (
                    <a hrefLang="it" lang="it" href={`tel:${phone.replace(/\s/g, '')}`} className={secondaryButtonClass}>
                        <Phone className="w-5 h-5"/>
                        <span>Chiama</span>
                    </a>
                )
            )}
        </div>

        {useLeadDialog && (
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-xl">
                    <DialogHeader>
                        <DialogTitle>
                            {mode === "whatsapp" ? "Contattaci su WhatsApp" : "Prenota un appuntamento"}
                        </DialogTitle>
                    </DialogHeader>
                    <LeadDialogForm
                        title={sedeTitle}
                        provincia={provincia}
                        mode={mode}
                        onSubmitted={() => setOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        )}
        </>
    );
}
